import { Storage } from '@/enums/storage.enum';
import { Login } from '@/interfaces/login';
import { StructureNode } from '@/interfaces/structureNode';
import { authResource, AuthResource } from '@/resources/auth.resource';
import { profileResource } from '@/resources/profile.resource';
import { AbstractModule } from './abstract.module';
import checkPermission from './auth/checkPermission';

/**
 * Moduł autentykacji
 *
 * @class AuthModule
 * @extends {AbstractModule<AuthResource>}
 */
class AuthModule extends AbstractModule<AuthResource> {
  public modules = { checkPermission };

  /**
   * Dane zalogowanego użytkownika
   *
   * @memberof AuthModule
   */
  public state = { data: undefined, nodes: [], token: localStorage.getItem(Storage.TOKEN) };

  public mutations = {
    /**
     * Zapisuje token
     *
     * @param {*} localState
     * @param {string} token
     */
    saveToken(localState: any, token: string): void {
      localState.token = token;
      localStorage.setItem(Storage.TOKEN, token);
    },

    /**
     * Czyści zapisane dane użytkownika
     *
     * @param {*} localState
     */
    clearToken(localState: any): void {
      localState.token = undefined;
      localStorage.removeItem(Storage.TOKEN);
    },

    /**
     * Zapisuje dane
     *
     * @param {*} localState
     * @param {*} data
     */
    saveData(localState: any, data: any): void {
      localState.data = data;
      localStorage.setItem(Storage.PROFILE_ID, data.id.toString());
    },

    /**
     * Czyści zapisane dane użytkownika
     *
     * @param {*} localState
     */
    clearData(localState: any): void {
      localState.data = undefined;
      localState.nodes = [];
      localStorage.clear();
      sessionStorage.clear();
    },

    /**
     * Zapisuje węzły użytkownika
     *
     * @param {*} localState
     * @param {any[]} nodes
     */
    saveNodes(localState: any, nodes: any[]): void {
      nodes.forEach((node: StructureNode) => {
        node.nameWithTypeDescription = `${node.name}  ${node.typeDescription ? `(${node.typeDescription})` : ''}`;
      });
      localState.nodes = nodes;
    }
  };
  public actions = {
    /**
     * Logowanie
     *
     * @param context context
     * @param data dane logowania
     */
    login: async (context: any, data: Login): Promise<void> => {
      const response = await this.resource.login(data.login, data.password);
      context.commit('saveToken', response.token);
      await profileResource.getSecretaryNodes().then(nodes => context.commit('saveNodes', nodes));
      if (response.profile.id) {
        localStorage.setItem(Storage.PROFILE_ID, response.profile.id.toString());
      }
      context.commit('saveData', response.profile);
    },

    /**
     * Wczytuje dane profilu
     *
     * @param context context
     */
    loadData: async (context: any): Promise<void> => {
      if (context.state.data) {
        return;
      }
      await Promise.all([profileResource.get(undefined as any), profileResource.getSecretaryNodes()]).then(
        ([profile, nodes]) => {
          context.commit('saveData', profile);
          context.commit('saveNodes', nodes);
        }
      );
    },

    /**
     * Wylogowanie
     *
     * @param context context
     */
    logout: (context: any) => {
      context.commit('clearToken');
      context.commit('clearPermissions');
      context.commit('clearData');
    }
  };

  public getters = {
    paymentListStructures: (state: any) =>
      state.nodes?.filter((node: any) => (node.isSingle && node.parentId) || (!node.isSingle && !node.parentId)) || []
  };
}

export default new AuthModule(authResource);
